import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';

const impactMeta = {
  critical: {
    color: 'bg-error text-error-foreground',
    icon: 'AlertTriangle',
    label: 'Critical'
  },
  recommended: {
    color: 'bg-warning text-warning-foreground',
    icon: 'Info',
    label: 'Recommended'
  },
  bonus: {
    color: 'bg-accent text-accent-foreground',
    icon: 'Plus',
    label: 'Bonus'
  }
};

const strengthMeta = {
  expert: { color: 'bg-success text-success-foreground', label: 'expert' },
  advanced: { color: 'bg-success text-success-foreground', label: 'advanced' },
  high: { color: 'bg-success text-success-foreground', label: 'high' },
  medium: { color: 'bg-warning text-warning-foreground', label: 'medium' },
  intermediate: { color: 'bg-warning text-warning-foreground', label: 'intermediate' },
  low: { color: 'bg-muted text-muted-foreground', label: 'low' },
  beginner: { color: 'bg-muted text-muted-foreground', label: 'beginner' }
};

const clampPercent = (value, fallback = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (num < 0) return 0;
  if (num > 100) return 100;
  return Math.round(num);
};

const KeywordAnalysis = ({
  foundSkills = [],
  missingSkills = []
}) => {
  const [activeTab, setActiveTab] = useState('found');

  const normalizedFound = useMemo(
    () =>
      foundSkills.map((skill, index) => {
        if (typeof skill === 'string') {
          return {
            name: skill,
            category: 'Skill',
            frequency: 65,
            strength: 'medium',
            evidence: ''
          };
        }

        const rawStrength =
          skill.strength ||
          skill.proficiency ||
          skill.proficiency_level ||
          skill.level ||
          '';
        const strengthKey = Object.keys(strengthMeta).find(
          (key) => key.toLowerCase() === String(rawStrength).toLowerCase()
        );
        const strength = strengthMeta[strengthKey] || strengthMeta.medium;

        return {
          name: skill.name || skill.skill_name || `Skill ${index + 1}`,
          category: skill.category || 'Skill',
          frequency: clampPercent(
            skill.frequency ??
              skill.marketDemand ??
              skill.confidence_score * 100 ??
              skill.score ??
              65
          ),
          strength: strength.label,
          badgeClass: strength.color,
          evidence: skill.evidence || skill.context || ''
        };
      }),
    [foundSkills]
  );

  const normalizedMissing = useMemo(
    () =>
      missingSkills.map((skill, index) => {
        if (typeof skill === 'string') {
          return {
            name: skill,
            category: 'Skill gap',
            impact: 'recommended',
            marketDemand: undefined,
            reason: ''
          };
        }

        const impactKey = (skill.impact || skill.severity || 'recommended').toLowerCase();
        const impact = impactMeta[impactKey] || impactMeta.recommended;

        return {
          name: skill.name || `Missing Skill ${index + 1}`,
          category: skill.category || 'High-impact gap',
          impactKey,
          impact,
          marketDemand: Number.isFinite(skill.marketDemand || skill.market_demand)
            ? clampPercent(skill.marketDemand ?? skill.market_demand)
            : undefined,
          reason: skill.reason || skill.note || skill.explanation || ''
        };
      }),
    [missingSkills]
  );

  const renderEmptyState = (message) => (
    <div className="bg-muted/30 border border-border/60 rounded-lg p-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );

  const foundCount = normalizedFound.length;
  const missingCount = normalizedMissing.length;

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="border-b border-border">
        <div className="p-6 pb-0">
          <h3 className="text-lg font-semibold text-card-foreground mb-4">Keyword Analysis</h3>
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('found')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
                activeTab === 'found'
                  ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} />
                <span>Found Skills ({foundCount})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('missing')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
                activeTab === 'missing'
                  ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} />
                <span>Missing Skills ({missingCount})</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {activeTab === 'found' ? (
          foundCount === 0 ? (
            renderEmptyState(
              "We couldn't detect strong keywords in your resume. Try adding more role-aligned skills before re-running the analysis."
            )
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Skills identified in your resume with confidence scores based on how clearly they appear.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {normalizedFound.map((skill, index) => (
                  <div key={`${skill.name}-${index}`} className="bg-muted/50 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{skill.name}</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          skill.badgeClass || strengthMeta[skill.strength]?.color || strengthMeta.medium.color
                        }`}
                      >
                        {skill.strength}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">{skill.category}</div>
                    {skill.evidence && (
                      <p className="text-xs text-muted-foreground bg-background/60 rounded px-2 py-1">
                        {skill.evidence}
                      </p>
                    )}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-background rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-primary h-2 transition-all duration-300"
                          style={{ width: `${skill.frequency}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground">{skill.frequency}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )
        ) : missingCount === 0 ? (
          renderEmptyState(
            'No missing high-impact keywords detected for this role. Great job staying aligned!'
          )
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Skills that the job emphasises but are missing or weak in your resume.
            </p>

            <div className="space-y-3">
              {normalizedMissing.map((skill, index) => (
                <div key={`${skill.name}-${index}`} className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-3">
                        <Icon
                          name={skill.impact.icon}
                          size={16}
                          className="text-muted-foreground"
                        />
                        <span className="font-medium text-foreground">{skill.name}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${skill.impact.color}`}>
                          {skill.impact.label}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{skill.category}</div>
                      {skill.reason && (
                        <p className="text-xs text-muted-foreground italic">{skill.reason}</p>
                      )}
                    </div>
                    <div className="text-right space-y-1">
                      {Number.isFinite(skill.marketDemand) ? (
                        <>
                          <div className="text-sm font-medium text-foreground">
                            {skill.marketDemand}%
                          </div>
                          <div className="text-xs text-muted-foreground">Market demand</div>
                        </>
                      ) : (
                        <div className="text-xs text-muted-foreground">Qualitative gap</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg space-y-3">
              <h4 className="text-sm font-medium text-foreground">Impact levels</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {Object.entries(impactMeta).map(([key, meta]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Icon name={meta.icon} size={14} className={meta.color.split(' ')[0].replace('bg-', 'text-')} />
                    <span className="text-foreground">{meta.label}:</span>
                    <span className="text-muted-foreground">
                      {key === 'critical'
                        ? 'Must-have for the role'
                        : key === 'recommended'
                          ? 'Highly preferred by hiring teams'
                          : 'Nice-to-have differentiator'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KeywordAnalysis;